import { CheckCircle, Warning } from '@phosphor-icons/react';

const STEPS = [
  { id: 'created', label: 'Created', check: () => true },
  { id: 'screened', label: 'Screened', check: (c) => (c.screening_history?.length || 0) > 0, actionTab: 'screening', actionLabel: 'Screen Now' },
  { id: 'matches', label: 'Matches Reviewed', check: (c) => { const p = (c.match_alerts || []).filter(a => a.status === 'pending'); return (c.match_alerts || []).length === 0 || p.length === 0; }, actionTab: 'screening', actionLabel: 'Review Matches' },
  { id: 'risk', label: 'Risk Assessed', check: (c) => c.risk_score != null && c.risk_score > 0, actionTab: 'risk', actionLabel: 'Calculate Risk' },
  { id: 'monitoring', label: 'Monitoring', check: (c) => c.monitoring_enabled === true, actionTab: 'monitoring', actionLabel: 'Enable' },
  { id: 'media', label: 'Media Alert', check: (c) => c.media_alert_configured === true, actionTab: 'media', actionLabel: 'Set Up' },
  { id: 'resolved', label: 'Resolved', check: (c) => ['cleared', 'escalated', 'rejected'].includes(c.status) },
];

const SUGGESTIONS = {
  screened: { icon: '\uD83D\uDCA1', text: 'Run screening to check against sanctions and PEP databases.', bg: 'bg-blue-50 text-blue-800 border-blue-200' },
  matches: { icon: '\u26A0\uFE0F', text: 'match alerts pending resolution. Review each and resolve.', bg: 'bg-amber-50 text-amber-800 border-amber-200' },
  risk: { icon: '\uD83D\uDCA1', text: 'All matches resolved. Calculate risk score to complete assessment.', bg: 'bg-blue-50 text-blue-800 border-blue-200' },
  monitoring: { icon: '\uD83D\uDCA1', text: 'Enable ongoing monitoring for automatic re-screening on data updates.', bg: 'bg-blue-50 text-blue-800 border-blue-200' },
  media: { icon: '\uD83D\uDCA1', text: 'Set up Google Alert to monitor news mentions.', bg: 'bg-blue-50 text-blue-800 border-blue-200' },
};

export default function CaseProgressTracker({ caseData, onTabChange, onAction }) {
  if (!caseData) return null;
  const completed = STEPS.map(s => s.check(caseData));
  const firstIncomplete = completed.findIndex((c, i) => !c && i > 0);
  const suggestion = firstIncomplete > 0 ? SUGGESTIONS[STEPS[firstIncomplete].id] : null;
  const pendingAlerts = (caseData.match_alerts || []).filter(a => a.status === 'pending').length;

  return (
    <div className="bg-white border border-[#E4E4E7] rounded-md p-4 mb-4" data-testid="case-progress-tracker">
      <div className="flex items-center gap-1">
        {STEPS.map((s, i) => (
          <div key={s.id} className="flex items-center flex-1">
            <div className="flex flex-col items-center">
              {completed[i] ? (
                <div className="relative w-7 h-7 rounded-full bg-green-500 flex items-center justify-center text-white text-[11px] font-bold border-2 border-green-500" data-testid={`progress-step-${i + 1}`}>
                  {i + 1}
                  <CheckCircle size={12} weight="fill" className="text-green-600 bg-white rounded-full absolute -top-1 -right-1" />
                </div>
              ) : i === firstIncomplete ? (
                <div className="relative w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-white text-[11px] font-bold border-2 border-amber-500" data-testid={`progress-step-${i + 1}`}>
                  {i + 1}
                  <Warning size={12} weight="fill" className="text-amber-600 bg-white rounded-full absolute -top-1 -right-1" />
                </div>
              ) : (
                <div className="w-7 h-7 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center text-gray-400 text-[11px] font-bold" data-testid={`progress-step-${i + 1}`}>
                  {i + 1}
                </div>
              )}
              <span className={`text-[10px] mt-1 text-center whitespace-nowrap ${completed[i] ? 'text-green-700 font-medium' : i === firstIncomplete ? 'text-amber-700 font-medium' : 'text-gray-400'}`}>{s.label}</span>
              {i === firstIncomplete && s.actionTab && (
                <button onClick={() => s.actionTab === 'screening' && onAction ? onAction(s.id) : onTabChange?.(s.actionTab)} className="text-[9px] text-[#0052FF] hover:underline mt-0.5">{s.actionLabel} \u2192</button>
              )}
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${completed[i] && completed[i+1] ? 'bg-green-400' : completed[i] ? 'bg-amber-300' : 'bg-gray-200'}`} />}
          </div>
        ))}
      </div>
      {suggestion && (
        <div className={`mt-3 px-3 py-2 rounded-md border text-xs ${suggestion.bg}`}>
          {suggestion.icon} {STEPS[firstIncomplete].id === 'matches' ? `${pendingAlerts} ${suggestion.text}` : suggestion.text}
        </div>
      )}
    </div>
  );
}
